import { useEffect, useState } from 'react'
import { Lock } from 'lucide-react'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import type { LineSupply, SetLinesSupplyBody, SupplyMode, SupplyOption } from './supply.api'
import { SUPPLY_MODES } from './supply-labels'

const NO_SUPPLIER = '__none__'

const buySchema = z.object({
  supplierReference: z.string().trim().max(128, 'La référence dépasse 128 caractères.'),
  requiredUnit: z.string().trim().max(16, "L'unité dépasse 16 caractères."),
  requiredQuantity: z
    .string()
    .trim()
    .refine((v) => v === '' || (/^\d+([.,]\d{1,4})?$/.test(v) && Number(v.replace(',', '.')) > 0), {
      message: 'Une quantité positive, 4 décimales au plus.',
    }),
})

interface LineSupplyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** « Four multifonction iQ700 », or « 3 lignes » for a bulk change. */
  title: string
  /** « Vendu : 1 · 1 249,00 € HT · importée de Winner ». */
  subtitle?: string | null
  /** The current supply of the line; a bulk change starts from « Acheter ». */
  initial?: LineSupply | null
  suppliers: SupplyOption[]
  /** A bulk change gives every line the same mode and supplier, never one reference or quantity. */
  bulk?: boolean
  /** Quantity sold, shown as the default quantity to order. */
  soldQuantity?: number | null
  onSave: (body: Omit<SetLinesSupplyBody, 'lineIds'>) => Promise<void>
}

/** SOLY-IMPORT-003 S11 — « Appro de la ligne » (maquette « Régler l'appro d'une ligne »). */
export function LineSupplyDialog({
  open, onOpenChange, title, subtitle, initial, suppliers, bulk = false, soldQuantity, onSave,
}: LineSupplyDialogProps) {
  const [mode, setMode] = useState<SupplyMode>('buy')
  const [supplierId, setSupplierId] = useState<string>(NO_SUPPLIER)
  const [reference, setReference] = useState('')
  const [quantity, setQuantity] = useState('')
  const [unit, setUnit] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setMode(bulk ? 'buy' : (initial?.mode ?? 'unqualified'))
    setSupplierId(initial?.supplierId ?? NO_SUPPLIER)
    setReference(initial?.supplierReference ?? '')
    setQuantity(initial?.requiredQuantity != null ? String(initial.requiredQuantity) : '')
    setUnit(initial?.requiredUnit ?? '')
    setError(null)
  }, [open, initial, bulk])

  const locked = !bulk && !!initial?.locked

  const handleSave = async () => {
    let body: Omit<SetLinesSupplyBody, 'lineIds'> = { mode }
    if (mode === 'buy') {
      const parsed = buySchema.safeParse({ supplierReference: reference, requiredUnit: unit, requiredQuantity: quantity })
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? 'Valeur invalide.')
        return
      }
      body = {
        mode,
        supplierId: supplierId === NO_SUPPLIER ? null : supplierId,
        ...(bulk
          ? {}
          : {
              supplierReference: parsed.data.supplierReference || null,
              requiredQuantity: parsed.data.requiredQuantity ? Number(parsed.data.requiredQuantity.replace(',', '.')) : null,
              requiredUnit: parsed.data.requiredUnit || null,
            }),
      }
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(body)
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "L'appro n'a pas été enregistré.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="line-supply-dialog">
        <SheetHeader className="border-b px-6 py-5">
          <p className="text-xs text-muted-foreground">{bulk ? 'Appro pour la sélection' : 'Appro de la ligne'}</p>
          <SheetTitle className="text-base">{title}</SheetTitle>
          <SheetDescription className={subtitle ? undefined : 'sr-only'}>
            {subtitle || 'Comment la ligne s’approvisionne.'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          <div className="flex gap-1 rounded-lg bg-muted p-1" role="radiogroup" aria-label="Appro">
            {SUPPLY_MODES.filter((m) => !bulk || m.mode !== 'unqualified').map((m) => (
              <button
                key={m.mode}
                type="button"
                role="radio"
                aria-checked={mode === m.mode}
                disabled={locked}
                onClick={() => setMode(m.mode)}
                data-testid={`line-supply-mode-${m.mode}`}
                className={cn(
                  'h-8 flex-1 rounded-md text-sm text-muted-foreground disabled:opacity-60',
                  mode === m.mode && 'bg-background font-semibold text-foreground shadow-sm',
                )}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode === 'buy' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="line-supply-supplier">Fournisseur</Label>
                <Select value={supplierId} onValueChange={setSupplierId} disabled={locked}>
                  <SelectTrigger id="line-supply-supplier" data-testid="line-supply-supplier" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SUPPLIER}>Fournisseur à préciser</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Seulement les fournisseurs de votre organisation.</p>
              </div>

              {!bulk ? (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="line-supply-reference">Référence à commander</Label>
                    <Input id="line-supply-reference" value={reference} onChange={(e) => setReference(e.target.value)}
                      maxLength={128} disabled={locked} data-testid="line-supply-reference" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="line-supply-quantity">Quantité à commander</Label>
                      <Input id="line-supply-quantity" inputMode="decimal" value={quantity}
                        placeholder={soldQuantity != null ? String(soldQuantity) : undefined}
                        onChange={(e) => setQuantity(e.target.value)} disabled={locked} data-testid="line-supply-quantity" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="line-supply-unit">Unité</Label>
                      <Input id="line-supply-unit" value={unit} placeholder="pc" maxLength={16}
                        onChange={(e) => setUnit(e.target.value)} disabled={locked} data-testid="line-supply-unit" />
                    </div>
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">
                    Vide : la quantité vendue. La quantité Winner reste visible dans « Origine Winner ».
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">La référence et la quantité se règlent ligne par ligne.</p>
              )}
            </>
          ) : null}

          {!bulk ? (
            <div className="flex items-start gap-2.5 rounded-lg bg-muted px-3.5 py-3 text-xs text-muted-foreground" data-testid="line-supply-lock-note">
              <Lock className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                {locked
                  ? 'Commande fournisseur envoyée : l’appro est figé. Annulez d’abord la commande chez le fournisseur.'
                  : 'Modifiable tant que la commande fournisseur est en brouillon. Une fois envoyée, l’appro est figé.'}
              </span>
            </div>
          ) : null}

          {error ? <p className="text-sm text-destructive" role="alert" data-testid="line-supply-error">{error}</p> : null}
        </div>

        <SheetFooter className="flex-row justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="line-supply-cancel">Annuler</Button>
          <Button onClick={handleSave} disabled={saving || locked} data-testid="line-supply-save">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
